import {
  SIDEBAR_MAX_WIDTH,
  SIDEBAR_MIN_WIDTH,
  SIDEBAR_WIDTH,
} from "@/components/layout/sidebarWidths.ts";
import { type SplitPanelLayoutProps, SplitPanelLayout } from "./SplitPanelLayout.tsx";
import { DEFAULT_PANEL_MIN_WIDTH } from "./useResizablePanel.tsx";

/**
 * localStorage key for the thread list's width. Shared by home and thread detail
 * (one key) so the split position is a single value that stays identical across
 * navigation — drag it on the calendar and the thread list and thread detail
 * open at the same width.
 *
 * Holds the sidebar's own width, which is what `persistedSide` below is for.
 * Only that number survives a change of display: the space beside it is a
 * different request at every window width.
 *
 * Its own name rather than the one the space-beside-it number used, because the
 * two are not interchangeable and a stale value is indistinguishable from a
 * fresh one by inspection. A sidebar is legitimately 320–380px wide and so was
 * many of the numbers written under the old name, so a value in that band would
 * be read as a deliberate choice and quietly narrow the column. The old key is
 * dropped in `clearClientState` rather than migrated: a divider position is
 * cheap to set again and expensive to get subtly wrong.
 *
 * Account-scoped, not permanent: `clearClientState` removes it on account
 * switch and logout wipes it with everything else, so a new session starts from
 * the resting default rather than inheriting the previous user's divider.
 */
export const THREAD_LIST_WIDTH_STORAGE_KEY = "threadListWidth";

/**
 * localStorage key for the divider between the page and the canvas column that
 * opens beside it, inside the right half of the split above.
 *
 * Its own key because the two dividers measure different things: the one above
 * is how wide the thread list is, this is how much of what's left the canvas
 * takes. Sharing one key would make dragging either move the other. Cleared on
 * the same terms as the key above.
 */
export const CANVAS_COLUMN_SPLIT_STORAGE_KEY = "canvasColumnSplitWidth";

/**
 * The canvas's own floor before any drag commits a px width. `SplitPanelLayout`
 * resolves the resting split as `max(this, 100cqw - restingMaxLeft)`, so naming
 * only the floor here is what makes the sidebar rest at exactly
 * {@link SIDEBAR_WIDTH} on every container wide enough to afford both.
 *
 * A px floor rather than a fraction of the container: a fraction would let the
 * canvas take its share first and leave the sidebar the remainder, and a list of
 * thread titles wants one width, not a ratio. Below the width where both fit,
 * this floor takes over and the canvas stops giving ground.
 */
export const CANVAS_MIN_WIDTH = "@3xl:[--panel-w-default:360px] @5xl:[--panel-w-default:380px]";

/**
 * How narrow the canvas column may be dragged — its own floor, not the shared
 * `DEFAULT_PANEL_MIN_WIDTH` the other splits fall back to. What this column
 * holds is a card, and a card stops being readable well before a page does.
 */
export const CANVAS_COLUMN_MIN_WIDTH = 380;

/**
 * Where the canvas column rests before any drag commits a px width — distinct
 * from {@link CANVAS_MIN_WIDTH} above, which is the *outer* split's floor and is
 * about how much room the thread list keeps.
 *
 * The same number as {@link CANVAS_COLUMN_MIN_WIDTH} wherever the container can
 * pay for it, so the column opens exactly where dragging it all the way in would
 * put it and double-clicking the divider returns it there. The two stay separate
 * mechanisms — a resting width is a CSS value, the floor clamps drags — which is
 * what lets them disagree where the container is too narrow for both columns.
 *
 * No breakpoint step of its own: a column this narrow is showing one card, and a
 * card is legible at a width rather than at a ratio. The drag bounds below are
 * about the column beside it rather than about this one.
 *
 * Capped so the page column beside it always keeps `DEFAULT_PANEL_MIN_WIDTH`.
 * Nothing else protects that column: a resting width is pure CSS, while
 * `minLeft` and `minRight` only clamp drags, so uncapped the page column is
 * whatever subtraction leaves it, which on an 800px window is 40px.
 *
 * `%` here, not the `cqw` that {@link SplitPanelLayoutProps.defaultWidthClassName}
 * otherwise requires, because this value only ever reaches the *nested* split:
 * there `100cqw` measures the screen's content area rather than this grid, and
 * `%` is the only unit that means the track's own container. That holds while
 * the nested split passes `pinLeftWidth={false}`, which is what keeps `--reserve`
 * from resolving the same value against the narrower left track.
 *
 * Both widths are literals because Tailwind generates arbitrary-property classes
 * by scanning source text. Interpolating the constants would emit no class at
 * all, with no type error and no failing test to catch it.
 */
export const CANVAS_COLUMN_DEFAULT_WIDTH = "@3xl:[--panel-w-default:min(380px,calc(100%-320px))]";

/**
 * The viewport width below which the thread list, the page and the canvas column
 * cannot all be on screen at once: the three columns' own floors, added up.
 *
 * Derived rather than written down, so it cannot drift from the numbers it is the
 * sum of. Below it the screen collapses the thread list rather than letting the
 * page absorb the shortfall, since the page is the column with no resting floor
 * of its own.
 */
export const THREE_COLUMN_MIN_VIEWPORT =
  SIDEBAR_WIDTH + DEFAULT_PANEL_MIN_WIDTH + CANVAS_COLUMN_MIN_WIDTH;

/**
 * How wide the canvas column may always be dragged, on any display.
 *
 * The ceiling the artifact sidebar used before its contents moved onto this
 * column, kept as the reach every display still affords: a laptop that could
 * take the canvas to 720 before still can. It is not a ceiling any more. What
 * the column shows is as often a document with a table in it as a card, and a
 * document reads better the wider it is, so on a display with width to spare
 * the canvas keeps growing past this until the page beside it is down to
 * {@link PAGE_COLUMN_MIN_WIDTH} — the margin the page had to spare is what the
 * canvas takes, never the page's own width.
 *
 * The page's hard floor still applies: on a container too narrow to afford
 * this reach beside `DEFAULT_PANEL_MIN_WIDTH`, the floor wins.
 */
export const CANVAS_COLUMN_REACH = 720;

/**
 * The width the page column keeps once the canvas is past
 * {@link CANVAS_COLUMN_REACH}.
 *
 * The page's design width: `pageColumnClasses[8]` is `max-w-180`, 45rem. The
 * conversation is laid out at that width and centred, so on a wide display
 * everything beyond it is margin, and that margin is what a drag hands the
 * canvas. Dragging never squeezes the conversation below the width it is
 * designed for.
 *
 * A floor only past the reach, not everywhere: as the page's floor outright it
 * would leave a 1470px laptop — 380 for the list, 720 here, 370 left — unable
 * to move the canvas at all, where today it reaches 720. Below the reach the
 * shared `DEFAULT_PANEL_MIN_WIDTH` stays the page's floor.
 */
export const PAGE_COLUMN_MIN_WIDTH = 720;

type CanvasSplitPanelLayoutProps = Omit<SplitPanelLayoutProps, "storageKey" | "persistedSide">;

/**
 * The shared canvas split: {@link SplitPanelLayout} bound to the canvas's divider
 * key and resting width, so home and thread detail share one split position
 * across navigation. Callers supply `left`/`right` and drive visibility
 * with `leftOpen`/`rightOpen` (home closes the left threads column, the thread
 * screen closes the right canvas column); everything else passes through.
 *
 * The sidebar rests at {@link SIDEBAR_WIDTH}, which is also its ceiling, and may
 * be dragged in as far as {@link SIDEBAR_MIN_WIDTH}. Both surfaces take all three
 * numbers, so the divider behaves identically wherever it is grabbed and the
 * thread detail column stops where the home sidebar does rather than stretching a
 * conversation across a wide display.
 *
 * Both bind only while both columns are open, which is the only time the divider
 * exists: a screen that closes the canvas still gives the sidebar the full width.
 */
export const CanvasSplitPanelLayout = ({
  defaultWidthClassName = CANVAS_MIN_WIDTH,
  minLeft = SIDEBAR_MIN_WIDTH,
  maxLeft = SIDEBAR_MAX_WIDTH,
  restingMaxLeft = SIDEBAR_WIDTH,
  ...rest
}: CanvasSplitPanelLayoutProps) => (
  <SplitPanelLayout
    storageKey={THREAD_LIST_WIDTH_STORAGE_KEY}
    // The sidebar is the column with a width of its own here; the space beside
    // it is whatever the screen has left. Remembering the sidebar's own number
    // is what makes a committed width mean the same thing on the next display.
    persistedSide="left"
    defaultWidthClassName={defaultWidthClassName}
    minLeft={minLeft}
    maxLeft={maxLeft}
    restingMaxLeft={restingMaxLeft}
    {...rest}
  />
);
